import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import useIndustries from '../hooks/useIndustries';

const API = 'https://carriera-backend.onrender.com/api';

const SOURCES = ['Indeed', 'LinkedIn', 'JobStreet', 'Referral', 'Walk-in', 'Website', 'Others'];

const GENDERS = ['Male', 'Female'];
const QUALIFICATIONS = ['SPM', 'STPM', 'Diploma', 'Bachelor\'s Degree', 'Master\'s Degree', 'PhD', 'Professional Cert', 'Others'];

const BLANK_FORM = {
  name: '', email: '', phone: '', location: '', address: '', ic_number: '',
  gender: '', year_of_birth: '', qualification: '',
  current_company: '', current_job_title: '', current_salary: '',
  industry: '', source: '', resume_url: '', notes: '',
  send_welcome_email: false
};

const INPUT = 'w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Tags are STORED lowercase (the backend normalises on write, so the exact-match
// tag filter can't fork into "Logistics" vs "logistics"). Capitalisation is a
// display concern only - never write displayTag() output back to the database.
function displayTag(tag) {
  return tag.replace(/\b[a-z]/g, c => c.toUpperCase());
}

function TagChip({ tag, onRemove }) {
  const color = tag.startsWith('industry:') ? 'bg-blue-50 text-blue-700 border-blue-200'
    : tag.startsWith('skill:') ? 'bg-amber-50 text-amber-700 border-amber-200'
    : tag.startsWith('level:') ? 'bg-purple-50 text-purple-700 border-purple-200'
    : tag.startsWith('availability:') ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-carriera-bg text-carriera-sec border-carriera-border';

  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 border rounded ${color}`}>
      {displayTag(tag)}
      {onRemove && (
        <button onClick={() => onRemove(tag)} className="ml-0.5 opacity-60 hover:opacity-100 leading-none">&times;</button>
      )}
    </span>
  );
}

function buildOpportunityDraft(job) {
  const industry = job.industry || job.clients?.industry || '';
  const overview = industry ? `An established company in the ${industry} sector` : 'One of our client companies';

  const subject = `A role that might suit you, ${job.title}`;

  const message = `Dear {{name}},

Greetings from Carriera! A role just opened up that may interest you. The details are as below:

{{job_table}}

If this sounds like something you'd be open to exploring, reply to this email with your updated resume and we will call you to share more.`;

  const jobInfo = {
    role: job.title,
    company_overview: overview,
    location: job.location || 'To be discussed'
  };

  return { subject, message, jobInfo };
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <p className="text-xs font-semibold text-carriera-muted uppercase tracking-widest mb-3 border-b border-carriera-border pb-1">{title}</p>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-carriera-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-xs text-carriera-muted w-24 flex-shrink-0">{label}</span>
      <span className="text-xs text-carriera-sec">{value}</span>
    </div>
  );
}

function Modal({ title, onClose, children, maxWidth = 'max-w-lg' }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white w-full ${maxWidth} max-h-screen overflow-y-auto`}
        style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-carriera-border">
          <h3 className="font-serif text-lg text-carriera-text">{title}</h3>
          <button onClick={onClose} className="text-carriera-muted hover:text-carriera-text text-xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function LinkedJobSheets({ candidateId, headers }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/job-sheets?archived=true`, { headers })
      .then(({ data }) => setSheets(data.filter(s => s.candidate_id === candidateId)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [candidateId]);

  if (loading) return <p className="text-xs text-carriera-muted">Loading...</p>;
  if (!sheets.length) return <p className="text-xs text-carriera-muted">No job sheets yet.</p>;

  const statusColors = {
    profile_sent: 'bg-blue-50 text-blue-700',
    interview_scheduled: 'bg-amber-50 text-amber-700',
    placed: 'bg-green-50 text-green-700',
    rejected: 'bg-red-50 text-red-500'
  };

  return (
    <div className="space-y-2">
      {sheets.map(s => (
        <div key={s.id} className="flex items-center justify-between text-xs border border-carriera-border px-3 py-2">
          <div>
            <p className="font-medium text-carriera-text">{s.jobs?.title || '—'}</p>
            <p className="text-carriera-muted">{s.jobs?.clients?.company_name || '—'}</p>
          </div>
          <div className="text-right">
            <span className={`px-2 py-0.5 rounded text-xs ${statusColors[s.status] || ''}`}>
              {s.status?.replace(/_/g, ' ')}
            </span>
            {s.archived && <p className="text-carriera-muted mt-0.5">archived</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Candidates() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [newTag, setNewTag] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(BLANK_FORM);
  const [adding, setAdding] = useState(false);

  const [blacklistModal, setBlacklistModal] = useState({ open: false, remove: false });
  const [blacklistReason, setBlacklistReason] = useState('');
  const [blacklisting, setBlacklisting] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [openJobs, setOpenJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [jobTableInfo, setJobTableInfo] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const { markClean, confirmClose } = useUnsavedChanges();
  const { industries, addIndustry } = useIndustries();

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (tagFilter) params.set('tag', tagFilter);
      const { data } = await axios.get(`${API}/candidates?${params}`, { headers });
      setCandidates(data);
    } catch (err) {
      console.error('Fetch candidates error:', err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, tagFilter]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  async function fetchDetail(id) {
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/candidates/${id}`, { headers });
      setDetail(data);
      setEditForm({
        name: data.name || '', email: data.email || '', phone: data.phone || '',
        location: data.location || '', address: data.address || '', ic_number: data.ic_number || '',
        gender: data.gender || '',
        year_of_birth: data.year_of_birth || '', qualification: data.qualification || '',
        current_company: data.current_company || '', current_job_title: data.current_job_title || '',
        current_salary: data.current_salary || '', industry: data.industry || '',
        source: data.source || '', resume_url: data.resume_url || '', notes: data.notes || ''
      });
    } catch (err) {
      console.error('Fetch detail error:', err);
    } finally {
      setDetailLoading(false);
    }
  }

  function selectCandidate(id) {
    setSelectedId(id);
    setEditing(false);
    fetchDetail(id);
  }

  async function saveEdit() {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/candidates/${selectedId}`, editForm, { headers });
      setDetail(d => ({ ...d, ...data }));
      setCandidates(cs => cs.map(c => c.id === selectedId ? { ...c, ...data } : c));
      setEditing(false);
    } catch {
      alert('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  }

  async function addTag() {
    const tag = newTag.trim();
    if (!tag || !detail) return;
    const tags = [...(detail.tags || []), tag];
    try {
      await axios.patch(`${API}/candidates/${selectedId}`, { tags }, { headers });
      setDetail(d => ({ ...d, tags }));
      setCandidates(cs => cs.map(c => c.id === selectedId ? { ...c, tags } : c));
      setNewTag('');
    } catch {
      alert('Failed to add tag.');
    }
  }

  async function removeTag(tag) {
    const tags = (detail.tags || []).filter(t => t !== tag);
    try {
      await axios.patch(`${API}/candidates/${selectedId}`, { tags }, { headers });
      setDetail(d => ({ ...d, tags }));
      setCandidates(cs => cs.map(c => c.id === selectedId ? { ...c, tags } : c));
    } catch {
      alert('Failed to remove tag.');
    }
  }

  async function handleAdd() {
    if (!addForm.name.trim()) { alert('Name is required.'); return; }
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/candidates`, addForm, { headers });
      if (data._deduped) {
        // Matched an existing candidate - record was updated, not duplicated.
        await fetchCandidates();
        alert(`${data.name} is already in your database - their record has been updated instead of creating a duplicate.`);
      } else {
        setCandidates(cs => [data, ...cs]);
      }
      setShowAdd(false);
      setAddForm(BLANK_FORM);
      selectCandidate(data.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add candidate.');
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete ${detail?.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/candidates/${selectedId}`, { headers });
      setCandidates(cs => cs.filter(c => c.id !== selectedId));
      setSelectedId(null);
      setDetail(null);
    } catch {
      alert('Failed to delete candidate.');
    }
  }

  async function submitBlacklist() {
    setBlacklisting(true);
    try {
      const { data } = await axios.patch(`${API}/candidates/${selectedId}/blacklist`, {
        blacklist: !blacklistModal.remove,
        reason: blacklistReason
      }, { headers });
      setDetail(d => ({ ...d, status: data.status, blacklist_reason: data.blacklist_reason }));
      setCandidates(cs => cs.map(c => c.id === selectedId ? { ...c, status: data.status } : c));
      setBlacklistModal({ open: false, remove: false });
      setBlacklistReason('');
    } catch {
      alert('Failed to update blacklist status.');
    } finally {
      setBlacklisting(false);
    }
  }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === candidates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(candidates.map(c => c.id)));
    }
  }

  function handleJobSelect(jobId) {
    setSelectedJobId(jobId);
    if (!jobId) {
      setJobTableInfo(null);
      return;
    }
    const job = openJobs.find(j => j.id === jobId);
    if (!job) return;
    const { subject, message, jobInfo } = buildOpportunityDraft(job);
    setEmailSubject(subject);
    setEmailMessage(message);
    setJobTableInfo(jobInfo);
  }

  async function sendBulkEmail() {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      alert('Subject and message are required.');
      return;
    }
    setSendingBulk(true);
    try {
      const { data } = await axios.post(`${API}/candidates/bulk-email`, {
        candidate_ids: [...selectedIds],
        subject: emailSubject,
        message: emailMessage,
        job_info: jobTableInfo
      }, { headers });
      setBulkResult(data);
    } catch {
      alert('Failed to send emails.');
    } finally {
      setSendingBulk(false);
    }
  }

  const allTags = [...new Set(candidates.flatMap(c => c.tags || []))].sort();

  return (
    <div className="flex gap-0" style={{ minHeight: '80vh' }}>
      <datalist id="industries-list">{industries.map(i => <option key={i} value={i} />)}</datalist>

      {/* Left: Table */}
      <div className="flex flex-col w-full transition-all">

        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs font-medium text-carriera-muted uppercase tracking-widest mb-1">Recruitment</p>
            <h2 className="font-serif text-3xl font-normal text-carriera-text">Candidates</h2>
          </div>
          <button onClick={() => { setShowAdd(true); setAddForm(BLANK_FORM); markClean(BLANK_FORM); }}
            className="bg-carriera-text text-white text-sm px-4 py-2 hover:bg-carriera-accent transition-colors">
            + Add Candidate
          </button>
        </div>

        <div className="flex gap-2 mb-3 flex-wrap">
          <input
            className="border border-carriera-border px-3 py-1.5 text-sm focus:outline-none focus:border-carriera-accent"
            style={{ minWidth: 200 }}
            placeholder="Search name, email, phone or tags..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="border border-carriera-border px-3 py-1.5 text-sm focus:outline-none focus:border-carriera-accent">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="blacklisted">Blacklisted</option>
          </select>
          <select value={tagFilter} onChange={e => setTagFilter(e.target.value)}
            className="border border-carriera-border px-3 py-1.5 text-sm focus:outline-none focus:border-carriera-accent">
            <option value="">All tags</option>
            {allTags.map(t => <option key={t} value={t}>{displayTag(t)}</option>)}
          </select>
          {(search || statusFilter || tagFilter) && (
            <button onClick={() => { setSearch(''); setStatusFilter(''); setTagFilter(''); }}
              className="text-xs text-carriera-muted hover:text-carriera-text px-2">
              Clear
            </button>
          )}
        </div>

        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 bg-carriera-text text-white px-4 py-2 mb-3 text-sm">
            <span>{selectedIds.size} selected</span>
            <button onClick={() => {
              setShowBulkEmail(true); setBulkResult(null); setEmailSubject(''); setEmailMessage(''); setSelectedJobId(''); setJobTableInfo(null);
              markClean({ subject: '', message: '' });
              if (openJobs.length === 0) {
                axios.get(`${API}/jobs`, { headers })
                  .then(({ data }) => setOpenJobs(data.filter(j => j.status === 'live')))
                  .catch(() => {});
              }
            }}
              className="bg-carriera-accent text-white text-xs px-3 py-1 hover:opacity-80">
              Send Opportunity Email
            </button>
            <button onClick={() => setSelectedIds(new Set())}
              className="ml-auto text-white/60 hover:text-white text-xs">Deselect all</button>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-carriera-muted py-8 text-center">Loading...</p>
        ) : (
          <div className="bg-white border border-carriera-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-carriera-border bg-carriera-bg">
                  <th className="px-3 py-2 text-left w-8">
                    <input type="checkbox"
                      checked={selectedIds.size > 0 && selectedIds.size === candidates.length}
                      onChange={toggleSelectAll} />
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-carriera-muted uppercase tracking-wide">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-carriera-muted uppercase tracking-wide">Contact</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-carriera-muted uppercase tracking-wide">Industry</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-carriera-muted uppercase tracking-wide">Location</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-carriera-muted uppercase tracking-wide">Exp</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-carriera-muted uppercase tracking-wide">Tags</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-carriera-muted uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody>
                {candidates.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-carriera-muted py-12 text-sm">No candidates found.</td>
                  </tr>
                )}
                {candidates.map(c => (
                  <tr key={c.id}
                    onClick={() => selectCandidate(c.id)}
                    className={`border-b border-carriera-border cursor-pointer hover:bg-carriera-bg transition-colors
                      ${selectedId === c.id ? 'bg-amber-50' : ''}`}>
                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => toggleSelect(c.id)} />
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-medium text-carriera-text">{c.name}</p>
                      {c.status === 'blacklisted' && (
                        <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">Blacklisted</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-xs text-carriera-sec">{c.email || '—'}</p>
                      <p className="text-xs text-carriera-muted">{c.phone || ''}</p>
                    </td>
                    <td className="px-3 py-3 text-xs text-carriera-sec">{c.industry || '—'}</td>
                    <td className="px-3 py-3 text-xs text-carriera-sec">{c.location || '—'}</td>
                    <td className="px-3 py-3 text-xs text-carriera-sec">{c.experience_years ? `${c.experience_years}y` : '—'}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).slice(0, 2).map(t => <TagChip key={t} tag={t} />)}
                        {(c.tags || []).length > 2 && (
                          <span className="text-xs text-carriera-muted">+{c.tags.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${c.status === 'blacklisted' ? 'bg-red-100 text-red-600' : 'bg-green-50 text-green-700'}`}>
                        {c.status || 'active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-2 border-t border-carriera-border">
              <p className="text-xs text-carriera-muted">{candidates.length} candidate{candidates.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        )}
      </div>

      {/* Candidate detail pop-up */}
      {selectedId && (
        <Modal
          title={detail?.name || 'Candidate'}
          maxWidth="max-w-2xl"
          onClose={() => {
            const close = () => { setSelectedId(null); setDetail(null); setEditing(false); };
            if (editing) confirmClose(editForm, close); else close();
          }}
        >
          {detailLoading ? (
            <p className="text-sm text-carriera-muted py-8">Loading...</p>
          ) : detail ? (
            <>
              <p className="text-xs text-carriera-muted -mt-1 mb-4">
                {detail.industry || '—'} · {detail.experience_years ? `${detail.experience_years} yrs exp` : 'exp n/a'}
              </p>

              {detail.status === 'blacklisted' && (
                <div className="bg-red-50 border border-red-200 px-3 py-2 mb-4 text-xs text-red-700">
                  <span className="font-semibold">Blacklisted</span>
                  {detail.blacklist_reason && <span> — {detail.blacklist_reason}</span>}
                </div>
              )}

              <div className="flex gap-2 mb-5 flex-wrap">
                {!editing ? (
                  <button onClick={() => { markClean(editForm); setEditing(true); }}
                    className="text-xs border border-carriera-border px-3 py-1.5 text-carriera-sec hover:border-carriera-accent transition-colors">
                    Edit
                  </button>
                ) : (
                  <>
                    <button onClick={saveEdit} disabled={saving}
                      className="text-xs bg-carriera-text text-white px-3 py-1.5 hover:bg-carriera-accent transition-colors disabled:opacity-40">
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => confirmClose(editForm, () => setEditing(false))}
                      className="text-xs border border-carriera-border px-3 py-1.5 text-carriera-sec hover:border-carriera-accent transition-colors">
                      Cancel
                    </button>
                  </>
                )}
                {detail.status === 'active' ? (
                  <button onClick={() => { setBlacklistModal({ open: true, remove: false }); setBlacklistReason(''); markClean(''); }}
                    className="text-xs border border-red-200 text-red-500 px-3 py-1.5 hover:bg-red-50 transition-colors">
                    Blacklist
                  </button>
                ) : (
                  <button onClick={() => setBlacklistModal({ open: true, remove: true })}
                    className="text-xs border border-green-200 text-green-600 px-3 py-1.5 hover:bg-green-50 transition-colors">
                    Remove Blacklist
                  </button>
                )}
                <button onClick={handleDelete}
                  className="text-xs border border-red-200 text-red-400 px-3 py-1.5 hover:bg-red-50 transition-colors">
                  Delete
                </button>
              </div>

              <Section title="Personal Details">
                {editing ? (
                  <div className="space-y-3">
                    <Field label="Name"><input className={INPUT} value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} /></Field>
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Gender">
                        <select className={INPUT} value={editForm.gender} onChange={e => setEditForm(f => ({...f, gender: e.target.value}))}>
                          <option value="">Select...</option>
                          {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </Field>
                      <Field label="Year of Birth"><input type="number" className={INPUT} value={editForm.year_of_birth} onChange={e => setEditForm(f => ({...f, year_of_birth: e.target.value}))} placeholder="e.g. 1990" /></Field>
                    </div>
                    <Field label="Qualification">
                      <select className={INPUT} value={editForm.qualification} onChange={e => setEditForm(f => ({...f, qualification: e.target.value}))}>
                        <option value="">Select...</option>
                        {QUALIFICATIONS.map(q => <option key={q} value={q}>{q}</option>)}
                      </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Contact Number"><input className={INPUT} value={editForm.phone} onChange={e => setEditForm(f => ({...f, phone: e.target.value}))} /></Field>
                      <Field label="Email"><input className={INPUT} value={editForm.email} onChange={e => setEditForm(f => ({...f, email: e.target.value}))} /></Field>
                    </div>
                    <Field label="Location"><input className={INPUT} value={editForm.location} onChange={e => setEditForm(f => ({...f, location: e.target.value}))} /></Field>
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="IC Number (No. K/P)"><input className={INPUT} value={editForm.ic_number} onChange={e => setEditForm(f => ({...f, ic_number: e.target.value}))} placeholder="e.g. 900101-14-5678" /></Field>
                      <Field label="Full Address (Alamat)"><input className={INPUT} value={editForm.address} onChange={e => setEditForm(f => ({...f, address: e.target.value}))} /></Field>
                    </div>
                    <Field label="Current Company"><input className={INPUT} value={editForm.current_company} onChange={e => setEditForm(f => ({...f, current_company: e.target.value}))} /></Field>
                    <Field label="Current Job Title"><input className={INPUT} value={editForm.current_job_title} onChange={e => setEditForm(f => ({...f, current_job_title: e.target.value}))} /></Field>
                    <Field label="Current Salary"><input className={INPUT} value={editForm.current_salary} onChange={e => setEditForm(f => ({...f, current_salary: e.target.value}))} placeholder="e.g. RM5,000" /></Field>
                    <Field label="Industry">
                      <input list="industries-list" className={INPUT} value={editForm.industry}
                        onChange={e => setEditForm(f => ({...f, industry: e.target.value}))}
                        onBlur={e => addIndustry(e.target.value)}
                        placeholder="Type or select..." />
                    </Field>
                    <Field label="Source">
                      <select className={INPUT} value={editForm.source} onChange={e => setEditForm(f => ({...f, source: e.target.value}))}>
                        <option value="">Select...</option>
                        {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Resume URL"><input className={INPUT} value={editForm.resume_url} onChange={e => setEditForm(f => ({...f, resume_url: e.target.value}))} /></Field>
                    <Field label="Notes"><textarea rows={3} className={INPUT + ' resize-none'} value={editForm.notes} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} /></Field>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Row label="Gender" value={detail.gender} />
                    <Row label="Year of Birth" value={detail.year_of_birth} />
                    <Row label="Qualification" value={detail.qualification} />
                    <Row label="Contact" value={detail.phone} />
                    <Row label="Email" value={detail.email} />
                    <Row label="Location" value={detail.location} />
                    <Row label="IC (No. K/P)" value={detail.ic_number} />
                    <Row label="Address" value={detail.address} />
                    <Row label="Current Co." value={detail.current_company} />
                    <Row label="Job Title" value={detail.current_job_title} />
                    <Row label="Salary" value={detail.current_salary} />
                    <Row label="Industry" value={detail.industry} />
                    <Row label="Source" value={detail.source} />
                    {detail.resume_url && (
                      <div className="flex gap-2">
                        <span className="text-xs text-carriera-muted w-24 flex-shrink-0">Resume</span>
                        <a href={detail.resume_url} target="_blank" rel="noreferrer"
                          className="text-xs text-carriera-accent hover:underline">View Resume →</a>
                      </div>
                    )}
                    {detail.notes && (
                      <div className="pt-1">
                        <p className="text-xs text-carriera-muted mb-1">Notes</p>
                        <p className="text-xs text-carriera-sec leading-relaxed whitespace-pre-wrap">{detail.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </Section>

              <Section title="Tags">
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(detail.tags || []).length === 0 && <p className="text-xs text-carriera-muted">No tags yet.</p>}
                  {(detail.tags || []).map(t => <TagChip key={t} tag={t} onRemove={removeTag} />)}
                </div>
                <div className="flex gap-2">
                  <input
                    className="flex-1 border border-carriera-border px-2 py-1 text-xs focus:outline-none focus:border-carriera-accent"
                    placeholder="e.g. industry:finance"
                    value={newTag}
                    onChange={e => setNewTag(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  />
                  <button onClick={addTag}
                    className="text-xs bg-carriera-text text-white px-3 py-1 hover:bg-carriera-accent transition-colors">
                    Add
                  </button>
                </div>
              </Section>

              <Section title="Job Sheets">
                <LinkedJobSheets candidateId={detail.id} headers={headers} />
              </Section>

              <Section title="Activity Log">
                {(detail.activity_log || []).length === 0 ? (
                  <p className="text-xs text-carriera-muted">No activity recorded yet.</p>
                ) : (
                  <div className="space-y-0">
                    {detail.activity_log.map((entry, idx) => (
                      <div key={entry.id} className="flex gap-3">
                        <div className="flex flex-col items-center pt-1">
                          <div className="w-2 h-2 rounded-full bg-carriera-accent flex-shrink-0" />
                          {idx < detail.activity_log.length - 1 && (
                            <div className="flex-1 w-px bg-carriera-border mt-1" style={{ minHeight: 20 }} />
                          )}
                        </div>
                        <div className="pb-4 flex-1">
                          <p className="text-xs text-carriera-text leading-relaxed">{entry.details}</p>
                          <p className="text-xs text-carriera-muted mt-0.5">{formatDate(entry.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </>
          ) : null}
        </Modal>
      )}

      {/* Add Modal */}
      {showAdd && (
        <Modal title="Add Candidate" onClose={() => confirmClose(addForm, () => setShowAdd(false))}>
          <div className="space-y-3">
            <Field label="Full Name *">
              <input className={INPUT} value={addForm.name} onChange={e => setAddForm(f => ({...f, name: e.target.value}))} autoFocus />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Gender">
                <select className={INPUT} value={addForm.gender} onChange={e => setAddForm(f => ({...f, gender: e.target.value}))}>
                  <option value="">Select...</option>
                  {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
              <Field label="Year of Birth"><input type="number" className={INPUT} value={addForm.year_of_birth} onChange={e => setAddForm(f => ({...f, year_of_birth: e.target.value}))} placeholder="e.g. 1990" /></Field>
            </div>
            <Field label="Qualification">
              <select className={INPUT} value={addForm.qualification} onChange={e => setAddForm(f => ({...f, qualification: e.target.value}))}>
                <option value="">Select...</option>
                {QUALIFICATIONS.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contact Number"><input className={INPUT} value={addForm.phone} onChange={e => setAddForm(f => ({...f, phone: e.target.value}))} /></Field>
              <Field label="Email"><input type="email" className={INPUT} value={addForm.email} onChange={e => setAddForm(f => ({...f, email: e.target.value}))} /></Field>
            </div>
            <Field label="Location"><input className={INPUT} value={addForm.location} onChange={e => setAddForm(f => ({...f, location: e.target.value}))} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="IC Number (No. K/P)"><input className={INPUT} value={addForm.ic_number} onChange={e => setAddForm(f => ({...f, ic_number: e.target.value}))} placeholder="e.g. 900101-14-5678" /></Field>
              <Field label="Full Address (Alamat)"><input className={INPUT} value={addForm.address} onChange={e => setAddForm(f => ({...f, address: e.target.value}))} /></Field>
            </div>
            <Field label="Current Company"><input className={INPUT} value={addForm.current_company} onChange={e => setAddForm(f => ({...f, current_company: e.target.value}))} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Current Job Title"><input className={INPUT} value={addForm.current_job_title} onChange={e => setAddForm(f => ({...f, current_job_title: e.target.value}))} /></Field>
              <Field label="Current Salary"><input className={INPUT} value={addForm.current_salary} onChange={e => setAddForm(f => ({...f, current_salary: e.target.value}))} placeholder="e.g. RM5,000" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Industry">
                <input list="industries-list" className={INPUT} value={addForm.industry}
                  onChange={e => setAddForm(f => ({...f, industry: e.target.value}))}
                  onBlur={e => addIndustry(e.target.value)}
                  placeholder="Type or select..." />
              </Field>
              <Field label="Source">
                <select className={INPUT} value={addForm.source} onChange={e => setAddForm(f => ({...f, source: e.target.value}))}>
                  <option value="">Select...</option>
                  {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Resume URL"><input className={INPUT} value={addForm.resume_url} onChange={e => setAddForm(f => ({...f, resume_url: e.target.value}))} placeholder="https://..." /></Field>
            <Field label="Notes"><textarea rows={3} className={INPUT + ' resize-none'} value={addForm.notes} onChange={e => setAddForm(f => ({...f, notes: e.target.value}))} /></Field>
            <label className="flex items-center gap-2 text-sm text-carriera-sec cursor-pointer pt-1">
              <input type="checkbox" checked={addForm.send_welcome_email}
                onChange={e => setAddForm(f => ({...f, send_welcome_email: e.target.checked}))} />
              Send "We received your application" email to candidate
            </label>
          </div>
          <div className="flex gap-2 justify-end mt-5">
            <button onClick={() => confirmClose(addForm, () => setShowAdd(false))}
              className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">
              Cancel
            </button>
            <button onClick={handleAdd} disabled={adding}
              className="text-sm bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors disabled:opacity-40">
              {adding ? 'Adding...' : 'Add Candidate'}
            </button>
          </div>
        </Modal>
      )}

      {/* Blacklist Modal */}
      {blacklistModal.open && (
        <Modal title={blacklistModal.remove ? 'Remove Blacklist' : 'Blacklist Candidate'}
          onClose={() => confirmClose(blacklistReason, () => setBlacklistModal({ open: false, remove: false }))}>
          <p className="text-sm text-carriera-sec mb-4">
            {blacklistModal.remove
              ? `Remove ${detail?.name} from the blacklist?`
              : `Blacklist ${detail?.name}? They will be excluded from bulk emails.`}
          </p>
          {!blacklistModal.remove && (
            <div className="mb-4">
              <label className="block text-xs text-carriera-muted mb-1">Reason (optional)</label>
              <input className={INPUT} value={blacklistReason} onChange={e => setBlacklistReason(e.target.value)}
                placeholder="e.g. No-show for interview" autoFocus />
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={() => confirmClose(blacklistReason, () => setBlacklistModal({ open: false, remove: false }))}
              className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">
              Cancel
            </button>
            <button onClick={submitBlacklist} disabled={blacklisting}
              className={`text-sm text-white px-4 py-2 transition-colors disabled:opacity-40
                ${blacklistModal.remove ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'}`}>
              {blacklisting ? 'Saving...' : (blacklistModal.remove ? 'Remove Blacklist' : 'Confirm')}
            </button>
          </div>
        </Modal>
      )}

      {/* Bulk Email Modal */}
      {showBulkEmail && (
        <Modal title={`Send Opportunity Email (${selectedIds.size} candidates)`}
          onClose={() => confirmClose({ subject: emailSubject, message: emailMessage }, () => { setShowBulkEmail(false); setBulkResult(null); })}>
          {bulkResult ? (
            <div>
              <div className={`px-4 py-3 mb-4 ${bulkResult.failed?.length ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                <p className="text-sm font-medium text-carriera-text">
                  ✓ Sent to {bulkResult.sent} candidate{bulkResult.sent !== 1 ? 's' : ''}
                </p>
                {bulkResult.failed?.length > 0 && (
                  <p className="text-xs text-red-600 mt-1">Failed: {bulkResult.failed.join(', ')}</p>
                )}
              </div>
              <div className="flex justify-end">
                <button onClick={() => { setShowBulkEmail(false); setBulkResult(null); setSelectedIds(new Set()); }}
                  className="text-sm bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors">
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-carriera-muted mb-4">
                Use <code className="bg-carriera-bg px-1 py-0.5 rounded">{'{{name}}'}</code> to personalise with each candidate's name, and <code className="bg-carriera-bg px-1 py-0.5 rounded">{'{{job_table}}'}</code> to insert the role details as a styled table (auto-filled when you pick a position below).
              </p>
              <div className="mb-4">
                <label className="block text-xs text-carriera-muted mb-1">Draft from an open position (optional)</label>
                <select className={INPUT} value={selectedJobId} onChange={e => handleJobSelect(e.target.value)}>
                  <option value="">— Select a position to pre-draft the email —</option>
                  {openJobs.map(j => (
                    <option key={j.id} value={j.id}>
                      {j.title}{j.clients?.company_name ? ` — ${j.clients.company_name}` : ''}
                    </option>
                  ))}
                </select>
                {openJobs.length === 0 && (
                  <p className="text-xs text-carriera-muted mt-1">No open (live) positions found — you can still write a custom message below.</p>
                )}
              </div>
              <div className="mb-3">
                <label className="block text-xs text-carriera-muted mb-1">Subject *</label>
                <input className={INPUT} value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                  placeholder="Exciting career opportunity — [Job Title]" autoFocus />
              </div>
              <div className="mb-4">
                <label className="block text-xs text-carriera-muted mb-1">Message *</label>
                <textarea rows={8} className={INPUT + ' resize-none'} value={emailMessage}
                  onChange={e => setEmailMessage(e.target.value)}
                  placeholder={'Dear {{name}},\n\nI hope this message finds you well.\n\nWe have an exciting opportunity that may interest you...'} />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => confirmClose({ subject: emailSubject, message: emailMessage }, () => setShowBulkEmail(false))}
                  className="text-sm border border-carriera-border px-4 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">
                  Cancel
                </button>
                <button onClick={sendBulkEmail} disabled={sendingBulk}
                  className="text-sm bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors disabled:opacity-40">
                  {sendingBulk ? 'Sending...' : `Send to ${selectedIds.size} candidates`}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
